import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Settings as SettingsIcon, CheckCircle, Plus, X } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const MobileStoreSettings = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getStore, updateStore, user } = useStore();
    const { showAlert } = useAlert();

    const [itemNames, setItemNames] = useState(['']);
    const [isSaved, setIsSaved] = useState(false);
    const [storeName, setStoreName] = useState('');

    // 비로그인 시 로그인 페이지로
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // master(admin) 계정은 모든 매장 설정 가능 → 리다이렉트 없음
        // 매니저는 자기 매장만 설정 가능
        if (user.role === 'manager' && String(user.storeId) !== String(id)) {
            showAlert('자신이 담당하는 매장의 설정만 변경할 수 있습니다.');
            navigate(`/store/${user.storeId}/mobile-settings`);
        }
    }, [user, navigate, id]);

    // 매장 데이터 로드
    useEffect(() => {
        const store = getStore(id);
        if (store) {
            setStoreName(store.name);
            const defaultItems = ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'];
            setItemNames(store.items && store.items.length > 0 ? store.items : defaultItems);
        }
    }, [id, getStore]);

    // ─── 핵심품목 관리 ───
    const handleItemNameChange = (index, value) => {
        const newNames = [...itemNames];
        newNames[index] = value;
        setItemNames(newNames);
    };

    const addItem = () => {
        if (itemNames.length >= 40) {
            showAlert('최대 40개 항목까지만 등록할 수 있습니다.');
            return;
        }
        setItemNames([...itemNames, '']);
    };

    const removeItem = (index) => {
        if (itemNames.length <= 1) {
            showAlert('최소 1개 이상의 항목은 유지해야 합니다.');
            return;
        }
        setItemNames(itemNames.filter((_, i) => i !== index));
    };

    // ─── 저장 ───
    const handleSave = () => {
        updateStore(id, { items: itemNames });
        // 설정 완료 플래그 → Dashboard에서 다시 설정 페이지로 안 보냄
        sessionStorage.setItem(`settingsDone_${id}`, 'true');
        setIsSaved(true);
        setTimeout(() => {
            navigate('/dashboard');
        }, 1500);
    };

    return (
        <Layout>
            <div className="management-content">
                <div className="card-header">
                    <h1>📋 매장 항목 설정</h1>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div className="submit-badge pending" style={{ fontSize: '0.8rem' }}>
                            {storeName}
                        </div>
                        {isSaved && (
                            <span className="submit-badge">
                                <CheckCircle size={16} /> 저장 완료
                            </span>
                        )}
                    </div>
                </div>

                <div className="glass-card management-card">
                    {/* ── 핵심품목 섹션 ── */}
                    <div className="settings-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <SettingsIcon size={22} color="#38bdf8" />
                            <h3>핵심 품목 이름 설정</h3>
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>({itemNames.length}/40)</span>
                        </div>
                        <button
                            onClick={addItem}
                            className="submit-badge"
                            style={{ background: 'rgba(56,189,248,0.2)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.5)', cursor: 'pointer', padding: '6px 12px' }}
                        >
                            <Plus size={15} style={{ marginRight: '4px' }} /> 추가
                        </button>
                    </div>
                    <p className="settings-desc" style={{ marginBottom: '20px' }}>
                        재고 현황과 발주 관리에 표시되는 품목 이름입니다. 변경해도 과거 날짜의 기록은 유지됩니다.
                    </p>

                    <div className="settings-form" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '36px' }}>
                        {itemNames.map((name, index) => (
                            <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: '#64748b', minWidth: '28px', textAlign: 'right' }}>
                                    {index + 1}.
                                </span>
                                <input
                                    type="text"
                                    value={name}
                                    placeholder={`품목 ${index + 1} 이름`}
                                    onChange={(e) => handleItemNameChange(index, e.target.value)}
                                    style={{ flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1rem' }}
                                />
                                <button
                                    onClick={() => removeItem(index)}
                                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', borderRadius: '8px', padding: '10px 10px', cursor: 'pointer' }}
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* 저장 버튼 */}
                    <button className="full-width-button" onClick={handleSave} style={{ marginTop: '8px' }}>
                        <Save size={18} style={{ marginRight: '8px' }} /> 설정 저장하기
                    </button>

                    <div className="glass-card info-card" style={{ marginTop: '24px' }}>
                        <h4>💡 참고</h4>
                        <p>품목 이름을 바꿔도 과거 날짜에 이미 저장된 데이터는 변경되지 않습니다. 오늘부터의 보고에 새 이름이 적용됩니다.</p>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default MobileStoreSettings;
